import React, { useRef, useState } from 'react';
import renderIf from 'render-if';
import { connect } from 'react-redux';
import styles from '../Dashboards.mod.less';
import classnames from 'classnames';
import { Overlay, Popover } from '../../legacy-bootstrap';
import { openReport } from '../helpers';

interface PanelData {
  id: string;
  type: string;
  [key: string]: any;
}

interface MoreMenuProps {
  bookmarkId?: string;
  panel: PanelData;
  editPanel: (e: React.MouseEvent, panel: PanelData) => void;
  deletePanel: (panelId: string) => void;
  preview?: boolean;
  canEdit: boolean;
}

interface StateProps {
  filters: any;
}

const MoreMenu = ({
  bookmarkId,
  panel,
  editPanel,
  deletePanel,
  preview,
  canEdit,
}: MoreMenuProps): JSX.Element | null => {
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [overlayPlacement] = useState<string>('bottom');
  const overlayTargetRef = useRef<HTMLButtonElement>(null);

  if (preview) {
    return null;
  }

  if (!canEdit && panel.type === 'RichTextPanel') {
    return null;
  }

  const canEditReport = canEdit && panel.type !== 'RichTextPanel';

  // Bookmark types that viewer users should NOT be able to open as standalone reports
  const VIEWER_RESTRICTED_BOOKMARKS: string[] = [
    'Bookmarks::IdeaPortalsWatchers',
    'Bookmarks::IdeaStatusChart',
  ];

  const NON_OPENABLE_PANEL_TYPES: string[] = [
    'ExtensionPanel',
    'SectionHeaderPanel',
    'RichTextPanel',
  ];

  const canOpenReport = !NON_OPENABLE_PANEL_TYPES.includes(panel.type);

  // Determine if user can open this report
  const isViewerRestrictedBookmark = VIEWER_RESTRICTED_BOOKMARKS.includes(
    panel.type
  );
  const canOpenReportForUser =
    canOpenReport && (canEdit || !isViewerRestrictedBookmark);

  // Don't render menu if no options are available
  const hasMenuOptions = canOpenReportForUser || canEditReport || canEdit;
  if (!hasMenuOptions) {
    return null;
  }

  return (
    <div className={styles.MoreMenuWrapper}>
      <button
        ref={overlayTargetRef}
        className={classnames(styles.MoreMenu, {
          [styles.MoreMenuFocused]: popoverVisible,
        })}
        onClick={() => setPopoverVisible(!popoverVisible)}
        aria-label='show more menu'
      >
        <i
          className={classnames(
            styles.MoreMenuIcon,
            'fa-regular',
            'fa-ellipsis'
          )}
        />
      </button>

      <Overlay
        target={() => overlayTargetRef.current}
        placement={overlayPlacement}
        show={popoverVisible}
        onHide={() => setPopoverVisible(false)}
        rootClose
      >
        <Popover
          id='moreMenuPopover'
          aria-label='more menu'
          className={styles.Popover}
        >
          <div className={styles.PopoverGraceZone} />
          <ul className={`${styles.Menu} ${styles.MenuAction}`}>
            {renderIf(canOpenReportForUser)(
              <li
                className={styles.MenuElement}
                onClick={(e: React.MouseEvent) => {
                  openReport(panel, bookmarkId);
                  setPopoverVisible(false);
                }}
              >
                Open report
              </li>
            )}
            {renderIf(canEditReport)(
              <li
                className={styles.MenuElement}
                onClick={(e: React.MouseEvent) => {
                  editPanel(e, panel);
                  setPopoverVisible(false);
                }}
              >
                <i className='fa-regular fa-pencil' /> Edit panel
              </li>
            )}
          </ul>
          {renderIf(canEdit)(
            <ul
              className={classnames(styles.Menu, {
                [styles.MenuAction]: !canEditReport,
              })}
            >
              <li
                className={styles.MenuElement}
                onClick={(e: React.MouseEvent) => {
                  setPopoverVisible(false);
                  deletePanel(panel.id);
                }}
              >
                <i className='fa-regular fa-trash-can' /> Delete panel
              </li>
            </ul>
          )}
        </Popover>
      </Overlay>
    </div>
  );
};

const mapStateToProps = (state: any): StateProps => ({
  filters: state.getIn(['meta', 'filters']),
});

export default connect<StateProps, {}, MoreMenuProps>(mapStateToProps)(
  MoreMenu
);
